import React, { useState } from "react";
import { Box, Typography, Paper, Grid, Tabs, Tab, Divider, Card, CardContent, Stack, Chip } from "@mui/material";
import TableSection from "../TableSection";
import { BarChart, PieChart } from "../Charts";
import { formatBRL, formatNumber, formatPercent } from "../utils/formatters";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import InventoryIcon from '@mui/icons-material/Inventory';

// Componente de resumo para mostrar informações destacadas
function SummaryCard({ title, value, description, icon, color }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{
            backgroundColor: `${color}15`,
            borderRadius: '50%',
            p: 1,
            mr: 1.5,
            display: 'flex'
          }}>
            {icon}
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function VendasDesempenhoTab({
  salesByCategory,
  salesByBrand,
  salesByRegion,
  skuPerformance
}) {
  // Estado para controlar a tab de detalhamento
  const [detailTab, setDetailTab] = useState(0);

  // Calcular métricas de resumo
  const calcularResumo = () => {
    // Total de vendas de todas as categorias
    const totalVendas = salesByCategory?.reduce((sum, category) => sum + category.totalSales, 0) || 0;

    // Total de receita de todas as categorias
    const totalReceita = salesByCategory?.reduce((sum, category) => sum + category.totalRevenue, 0) || 0;

    // Total de pedidos de todas as regiões
    const totalPedidos = salesByRegion?.reduce((sum, region) => sum + region.orderCount, 0) || 0;

    // Categoria mais vendida
    let categoriaMaisVendida = { nome: "-", vendas: 0 };
    if (salesByCategory && salesByCategory.length > 0) {
      const catMaisVendida = salesByCategory.reduce((prev, current) =>
        (prev.totalSales > current.totalSales) ? prev : current);
      categoriaMaisVendida = {
        nome: catMaisVendida.categoryName,
        vendas: catMaisVendida.totalSales
      };
    }

    // Marca mais vendida
    let marcaMaisVendida = { nome: "-", vendas: 0 };
    if (salesByBrand && salesByBrand.length > 0) {
      const brandMaisVendida = salesByBrand.reduce((prev, current) =>
        (prev.totalSales > current.totalSales) ? prev : current);
      marcaMaisVendida = {
        nome: brandMaisVendida.brandName,
        vendas: brandMaisVendida.totalSales
      };
    }

    return {
      totalVendas,
      totalReceita,
      totalPedidos,
      categoriaMaisVendida,
      marcaMaisVendida
    };
  };

  const resumo = calcularResumo();

  // Alternar entre as abas de detalhamento
  const handleChangeDetailTab = (event, newValue) => {
    setDetailTab(newValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Cards de resumo */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total de vendas"
            value={formatNumber(resumo.totalVendas, 0)}
            description="Quantidade total de vendas no período"
            icon={<TrendingUpIcon sx={{ color: '#2e7d32' }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Receita total"
            value={formatBRL(resumo.totalReceita)}
            description="Valor total de vendas no período"
            icon={<ShowChartIcon sx={{ color: '#1976d2' }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total de pedidos"
            value={formatNumber(resumo.totalPedidos, 0)}
            description="Número total de pedidos processados"
            icon={<EqualizerIcon sx={{ color: '#9c27b0' }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Destaques */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Destaques de Desempenho
          </Typography>
          <Divider sx={{ flexGrow: 1, ml: 2 }} />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <CategoryIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Categoria mais vendida
              </Typography>
            </Stack>
            <Box sx={{ pl: 4 }}>
              <Typography variant="h6" fontWeight="bold">{resumo.categoriaMaisVendida.nome}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatNumber(resumo.categoriaMaisVendida.vendas, 0)} vendas
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <BusinessIcon color="secondary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Marca mais vendida
              </Typography>
            </Stack>
            <Box sx={{ pl: 4 }}>
              <Typography variant="h6" fontWeight="bold">{resumo.marcaMaisVendida.nome}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatNumber(resumo.marcaMaisVendida.vendas, 0)} vendas
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs de navegação para os detalhes */}
      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <Tabs
          value={detailTab}
          onChange={handleChangeDetailTab}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<CategoryIcon />}
            label="Categorias"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<BusinessIcon />}
            label="Marcas"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<PublicIcon />}
            label="Regiões"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<InventoryIcon />}
            label="SKUs"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
            disabled={!skuPerformance || !skuPerformance.skuPerformance}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Conteúdo da tab de Categorias */}
          {detailTab === 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
                Vendas por Categoria
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
                Total de vendas e receita agrupados por categoria no período selecionado.
              </Typography>

              {salesByCategory && salesByCategory.length > 0 ? (
                <Box sx={{ width: '100%', height: 400 }}>
                  <BarChart
                    xAxis={[{
                      scaleType: "band",
                      data: salesByCategory.map((c) => c.categoryName),
                      label: 'Categorias'
                    }]}
                    series={[
                      {
                        data: salesByCategory.map((c) => c.totalSales),
                        label: "Vendas",
                        color: '#1a237e'
                      },
                      {
                        data: salesByCategory.map((c) => c.totalRevenue / 100),
                        label: "Receita (R$ x100)",
                        color: '#43a047'
                      }
                    ]}
                    width={undefined}
                    height={350}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
                </Box>
              )}
            </>
          )}

          {/* Conteúdo da tab de Marcas */}
          {detailTab === 1 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <div>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                    Desempenho por Marca
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#607d8b' }}>
                    Análise detalhada das vendas por marca
                  </Typography>
                </div>
                <Chip
                  label={`${salesByBrand?.length || 0} marcas no período`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                  <TableSection
                    showTitle={false}
                    columns={[
                      { field: "brandName", headerName: "Marca" },
                      { field: "totalSales", headerName: "Vendas", format: (v) => formatNumber(v, 0) },
                      { field: "totalUnits", headerName: "Unidades", format: (v) => formatNumber(v, 0) },
                      { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) }
                    ]}
                    rows={salesByBrand || []}
                    emptyMessage="Nenhum dado disponível"
                  />
                </Grid>
                <Grid item xs={12} lg={5}>
                  {salesByBrand && salesByBrand.length > 0 ? (
                    <Box sx={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 2 }}>
                        Participação das marcas nas vendas totais
                      </Typography>
                      <PieChart
                        series={[{
                          data: salesByBrand.map((b) => ({
                            id: b.brandName,
                            value: b.totalSales,
                            label: b.brandName
                          })),
                          innerRadius: 30,
                          outerRadius: 120,
                          paddingAngle: 1,
                          cornerRadius: 3
                        }]}
                        width={undefined}
                        height={300}
                        slotProps={{
                          legend: {
                            direction: 'column',
                            position: { vertical: 'middle', horizontal: 'right' },
                            padding: 0
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Typography variant="body1" color="text.secondary">Nenhum dado disponível</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* Conteúdo da tab de Regiões */}
          {detailTab === 2 && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
                Vendas por Região
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
                Distribuição geográfica das vendas no período selecionado.
              </Typography>

              {salesByRegion && salesByRegion.length > 0 ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TableSection
                      showTitle={false}
                      columns={[
                        { field: "state", headerName: "Estado" },
                        { field: "orderCount", headerName: "Pedidos", format: (v) => formatNumber(v, 0) },
                        { field: "totalUnits", headerName: "Unidades", format: (v) => formatNumber(v, 0) },
                        { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) },                        {
                          field: "percentOfTotal",
                          headerName: "% do Total",
                          format: (v, row) => {
                            if (!row || typeof row.orderCount === 'undefined') return '0%';
                            const total = salesByRegion.reduce((sum, r) => sum + (r.orderCount || 0), 0);
                            return formatPercent((row.orderCount / (total || 1)) * 100);
                          }
                        }
                      ]}
                      rows={salesByRegion}
                      emptyMessage="Nenhum dado disponível"
                    />
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
                </Box>
              )}
            </>
          )}

          {/* Conteúdo da tab de SKUs */}
          {detailTab === 3 && skuPerformance && skuPerformance.skuPerformance && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <div>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                    Comparativo de SKUs: {skuPerformance.productTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#607d8b' }}>
                    Análise de desempenho dos diferentes SKUs deste produto
                  </Typography>
                </div>
                <Chip
                  label={`${skuPerformance.totalSkus || 0} SKUs disponíveis`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>

              <TableSection
                showTitle={false}
                columns={[
                  { field: "skuTitle", headerName: "SKU" },
                  { field: "totalSales", headerName: "Vendas", format: (v) => formatNumber(v, 0) },
                  { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) },
                  { field: "averagePrice", headerName: "Preço Médio", format: (v) => formatBRL(v) },
                  { field: "orderCount", headerName: "Pedidos", format: (v) => formatNumber(v, 0) },
                  { field: "salesPercentage", headerName: "% Vendas", format: (v) => formatPercent(v) },
                  { field: "revenuePercentage", headerName: "% Receita", format: (v) => formatPercent(v) },
                ]}
                rows={skuPerformance.skuPerformance}
                emptyMessage="Nenhum dado disponível"
              />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}